import { useEffect, useRef, type RefObject } from "react";

/** Below this width the rail is an off-canvas drawer (matches the 1023px breakpoint in app.css). */
export const DRAWER_QUERY = "(max-width: 1023px)";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Tabbable descendants in order. Roving-tabindex items (the tree menu's tabindex="-1" rows) are skipped:
 * Tab reaches the tree once, through its current row.
 */
export function focusableIn(box: HTMLElement): HTMLElement[] {
  return Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getAttribute("tabindex") !== "-1" && !el.closest("[hidden], [inert]") && el.getAttribute("aria-hidden") !== "true",
  );
}

/** True while a modal dialog other than the drawer itself is open (it owns the keyboard then). */
function otherModalOpen(): boolean {
  return !!document.querySelector('[role="dialog"][aria-modal="true"]:not(.rail)');
}

/**
 * Body scroll lock that also holds on iOS Safari (overflow: hidden alone does not stop the page behind from
 * scrolling there): the body is fixed at the current offset and the offset is restored on release.
 */
function lockBodyScroll(): (restoreTo: "previous" | "top") => void {
  const root = document.documentElement;
  const y = window.scrollY || root.scrollTop || 0;
  const st = document.body.style;
  const prev = { position: st.position, top: st.top, left: st.left, right: st.right, width: st.width, overflow: st.overflow };
  st.position = "fixed";
  st.top = `-${y}px`;
  st.left = "0";
  st.right = "0";
  st.width = "100%";
  st.overflow = "hidden";
  return (restoreTo) => {
    Object.assign(st, prev);
    root.scrollTop = restoreTo === "top" ? 0 : y;
  };
}

/**
 * Off-canvas drawer behaviour for the rail on phones and tablets (ADR 0030):
 * - focus moves to `initial` when it opens and Tab / Shift+Tab stay inside `panel`;
 * - Esc closes; the page behind is `inert` (no taps, no screen-reader reading) and does not scroll;
 * - while another modal dialog is open (e.g. opened from the drawer), Tab and Esc belong to that dialog;
 * - on close, focus returns to the element that opened it (the menu button) if focus was in the drawer,
 *   and the page keeps its scroll position;
 * - closed because of navigation (the caller calls the returned `navigated()` first): focus moves to the
 *   new page's main landmark instead and the page starts at the top;
 * - widening the window past the breakpoint closes it (the rail is then a normal sidebar).
 */
export function useDrawer({
  open,
  onClose,
  panel,
  initial,
  behind,
  main,
}: {
  open: boolean;
  onClose: () => void;
  panel: RefObject<HTMLElement>;
  initial: RefObject<HTMLElement>;
  /** The page content made inert while the drawer is open. */
  behind: RefObject<HTMLElement>;
  /** The main landmark (tabIndex -1) that takes focus after a navigation from the drawer. */
  main: RefObject<HTMLElement>;
}): { navigated: () => void } {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const byNavigation = useRef(false);

  useEffect(() => {
    if (!open) return;
    byNavigation.current = false;
    const opener = document.activeElement as HTMLElement | null;
    const box = panel.current;
    const page = behind.current;
    (initial.current ?? box)?.focus();
    page?.setAttribute("inert", "");
    const unlock = lockBodyScroll();

    const onKey = (e: KeyboardEvent) => {
      // A dialog opened from the drawer handles its own Escape and Tab.
      if ((e.key === "Escape" || e.key === "Tab") && otherModalOpen()) return;
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !box) return;
      const items = focusableIn(box);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && box.contains(active);
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);

    const mq = typeof window.matchMedia === "function" ? window.matchMedia(DRAWER_QUERY) : null;
    const onWide = (e: MediaQueryListEvent) => !e.matches && closeRef.current();
    mq?.addEventListener?.("change", onWide);

    return () => {
      document.removeEventListener("keydown", onKey);
      mq?.removeEventListener?.("change", onWide);
      page?.removeAttribute("inert");
      const navigated = byNavigation.current;
      unlock(navigated ? "top" : "previous");
      const focusWasInside = !!box && box.contains(document.activeElement);
      const lost = focusWasInside || document.activeElement === document.body;
      if (!lost) return;
      if (navigated) main.current?.focus({ preventScroll: true });
      else if (opener && document.contains(opener)) opener.focus();
    };
  }, [open, panel, initial, behind, main]);

  return { navigated: () => void (byNavigation.current = true) };
}
